import React from "react";
import PropTypes from "prop-types";
import { pluralize } from "utils/string-helpers";
import { Button } from "components/common/button";
import { DropdownModal } from "components/common/dropdown-modal";
import styles from "components/documents/multi-document-actions-container.scss";
import { ExportButton } from "components/documents/exports/export-button";
import { BatchEditForm } from "components/documents/modals/forms/batch-edit-form";
import { categoryType, currencyType, documentPropType, vendorType } from "custom-prop-types";
import { AssignCategoriesForm } from "components/documents/modals/forms/assign-categories-form";
import { TrashMultipleDocumentsModal } from "components/documents/modals/trash-multiple-documents-modal";
import { DeletePermanentlyModal } from "components/documents/modals/delete-permanently-modal";
import { MultiDocumentsActionsHamburger } from "components/documents/documents-grid/multi-documents-actions-hamburger";
import { mergeDocumentsColumnObjects } from "components/grid/column-objects";
import FaPencil from "react-icons/fa/pencil";
import FaHamburger from "react-icons/fa/bars";

export const MultiDocumentActionsContainer = (props) => {
  if (props.selectedDocumentIds.length > 0) {
    return content(props);
  }
  return null;
};

MultiDocumentActionsContainer.propTypes = {
  categories: PropTypes.arrayOf(categoryType).isRequired,
  currencies: PropTypes.arrayOf(currencyType).isRequired,
  documents: PropTypes.arrayOf(documentPropType).isRequired,
  isBatchCategoriesMode: PropTypes.bool.isRequired,
  isBatchDownloadLoading: PropTypes.bool.isRequired,
  isBatchEditLoading: PropTypes.bool.isRequired,
  isBatchEditMode: PropTypes.bool.isRequired,
  isMergeDocumentsLoading: PropTypes.bool.isRequired,
  isMergeDocumentsMode: PropTypes.bool.isRequired,
  isTrashMultipleDocumentsLoading: PropTypes.bool.isRequired,
  isTrashMultipleDocumentsVisible: PropTypes.bool.isRequired,
  onChangeViewedSetting: PropTypes.func.isRequired,
  onCreateCategory: PropTypes.func.isRequired,
  onEndBatchCategoriesMode: PropTypes.func.isRequired,
  onEndBatchDownloadMode: PropTypes.func.isRequired,
  onEndBatchEditMode: PropTypes.func.isRequired,
  onEndMergeDocumentsMode: PropTypes.func.isRequired,
  onMergeDocuments: PropTypes.func.isRequired,
  onStartBatchCategoriesMode: PropTypes.func.isRequired,
  onStartBatchDownloadMode: PropTypes.func.isRequired,
  onStartBatchEditMode: PropTypes.func.isRequired,
  onStartMergeDocumentsMode: PropTypes.func.isRequired,
  onSubmitBatchDownloadDocuments: PropTypes.func.isRequired,
  onSubmitBatchEdit: PropTypes.func.isRequired,
  onTrashDocument: PropTypes.func.isRequired,
  selectedDocumentIds: PropTypes.arrayOf(PropTypes.string),
  userEmail: PropTypes.string.isRequired,
  vendors: PropTypes.arrayOf(vendorType).isRequired
};

const content = ({
  accountId,
  accountSlug,
  categories,
  currencies,
  filters,
  glCodes,
  isBatchCategoriesMode,
  isBatchCategoriesLoading,
  isBatchDownloadMode,
  isBatchDownloadLoading,
  isBatchEditLoading,
  isBatchEditMode,
  isDeletePermanentlyLoading,
  isDeletePermanentlyVisible,
  isHamburgerMode,
  isMergeDocumentsLoading,
  isMergeDocumentsMode,
  isRestoreDocumentsLoading,
  isTrash,
  isTrashMultipleDocumentsLoading,
  isTrashMultipleDocumentsVisible,
  mergeDocument,
  onChangeViewedSetting,
  onCreateCategory,
  onDeletePermanently,
  onDeselectAllDocuments,
  onDeselectMergeDocument,
  onEndBatchCategoriesMode,
  onEndBatchDownloadMode,
  onEndBatchEditMode,
  onEndHamburgerMode,
  onEndMergeDocumentsMode,
  onMergeDocuments,
  onHideDeletePermanently,
  onHideTrashMultipleDocuments,
  onRestoreDocuments,
  onSelectAllDocuments,
  onShowDeletePermanently,
  onSelectMergeDocument,
  onSelectMergeField,
  onStartBatchCategoriesMode,
  onStartBatchDownloadMode,
  onStartBatchEditMode,
  onStartHamburgerMode,
  onStartMergeDocumentsMode,
  onSubmitBatchAssignCategories,
  onSubmitBatchDownloadDocuments,
  onSubmitBatchEdit,
  onShowTrashMultipleDocuments,
  onStartExportMode,
  onTrashMultipleDocuments,
  preferredCurrency,
  documents,
  selectedMergeDocumentId,
  selectedDocumentIds,
  userEmail,
  vendors
}) => {
  if (isTrash) {
    return (
      <div className={ styles.container }>
        <div className={ styles["trash-actions"] }>
          <span className={ styles.back } onClick={ onDeselectAllDocuments }>&#11013; Back</span>
          <span className={ styles["info-label"] }>
            { pluralize(selectedDocumentIds.length, "documents selected", "document selected") }
            {renderSelectAllButton(selectedDocumentIds, onSelectAllDocuments, accountId, filters)}
          </span>
          <Button loading={ isRestoreDocumentsLoading } onClick={ onRestoreDocuments }>Restore</Button>
          <Button danger onClick={ onShowDeletePermanently }>
            Delete Permanently
          </Button>
        </div>

        <DeletePermanentlyModal
          isDeletePermanentlyLoading={ isDeletePermanentlyLoading }
          isDeletePermanentlyVisible={ isDeletePermanentlyVisible }
          onDeletePermanently={ onDeletePermanently }
          onHideDeletePermanently={ onHideDeletePermanently }
          selectedIds={ selectedDocumentIds }
        />
      </div>
    );
  }
  return (
    <div className={ styles.container }>
      <span className={ styles.back } onClick={ onDeselectAllDocuments }>&#11013; Back</span>
      <span className={ styles["info-label"] }>
        {pluralize(selectedDocumentIds.length, "documents selected", "document selected")}
        {renderSelectAllButton(selectedDocumentIds, onSelectAllDocuments, accountId, filters)}
      </span>
      <div className={ styles["actions-buttons"] }>
        { batchEditButton(onStartBatchEditMode) }
        <DropdownModal offsetLeft={ -116 } offsetTop={ 20 } onClose={ onEndBatchEditMode } visible={ isBatchEditMode }>
          <div>
            <BatchEditForm
              currencies={ currencies }
              isLoading={ isBatchEditLoading }
              onClose={ onEndBatchEditMode }
              onSubmitBatchEdit={ onSubmitBatchEdit }
              vendors={ vendors }
            />
          </div>
        </DropdownModal>
        <Button onClick={ onStartBatchCategoriesMode }>Assign categories</Button>
        <DropdownModal
          offsetLeft={ -146 }
          offsetTop={ 20 }
          onClose={ onEndBatchCategoriesMode }
          visible={ isBatchCategoriesMode }
          width={ 350 }
        >
          <AssignCategoriesForm
            accountId={ accountId }
            categories={ categories }
            isLoading={ isBatchCategoriesLoading }
            onCancel={ onEndBatchCategoriesMode }
            onCreateCategory={ onCreateCategory }
            onSave={ onSubmitBatchAssignCategories }
          />
        </DropdownModal>
        <ExportButton
          accountSlug={ accountSlug }
          glCodes={ glCodes }
          onLinkAccount={ () => null }
          onStartExportMode={ onStartExportMode }
          selectedDocumentIds={ selectedDocumentIds }
        />

        <Button onClick={ onStartHamburgerMode }>
          <FaHamburger size={ 15 } />
        </Button>
        <DropdownModal
          offsetLeft={ -50 }
          offsetTop={ 20 }
          onClose={ onEndHamburgerMode }
          visible={ isHamburgerMode }
          width={ 180 }
        >
          <MultiDocumentsActionsHamburger
            columns={ mergeDocumentsColumnObjects }
            documents={ documents }
            isBatchDownloadLoading={ isBatchDownloadLoading }
            isBatchDownloadMode={ isBatchDownloadMode }
            isMergeDocumentsLoading={ isMergeDocumentsLoading }
            isMergeDocumentsMode={ isMergeDocumentsMode }
            mergeDocument={ mergeDocument }
            onChangeViewedSetting={ onChangeViewedSetting }
            onDeselectMergeDocument={ onDeselectMergeDocument }
            onEndBatchDownloadMode={ onEndBatchDownloadMode }
            onEndHamburgerMode={ onEndHamburgerMode }
            onEndMergeDocumentsMode={ onEndMergeDocumentsMode }
            onMergeDocuments={ onMergeDocuments }
            onSelectMergeDocument={ onSelectMergeDocument }
            onSelectMergeField={ onSelectMergeField }
            onStartBatchDownloadMode={ onStartBatchDownloadMode }
            onStartHamburgerMode={ onStartHamburgerMode }
            onStartMergeDocumentsMode={ onStartMergeDocumentsMode }
            onSubmitBatchDownloadDocuments={ onSubmitBatchDownloadDocuments }
            preferredCurrency={ preferredCurrency }
            selectedDocumentIds={ selectedDocumentIds }
            selectedMergeDocumentId={ selectedMergeDocumentId }
            userEmail={ userEmail }
          />
        </DropdownModal>

        <div className={ styles.trash }>
          <Button danger onClick={ onShowTrashMultipleDocuments }>Trash</Button>
          <TrashMultipleDocumentsModal
            isTrashMultipleDocumentsLoading={ isTrashMultipleDocumentsLoading }
            isTrashMultipleDocumentsVisible={ isTrashMultipleDocumentsVisible }
            onHideTrashMultipleDocuments={ onHideTrashMultipleDocuments }
            onTrashMultipleDocuments={ onTrashMultipleDocuments }
          />
        </div>
      </div>
    </div>
  );
};

content.propTypes = MultiDocumentActionsContainer.propTypes;

const batchEditButton = (onStartBatchEditMode) => (
  <Button onClick={ onStartBatchEditMode }>
    <span>
      <FaPencil className={ styles["edit-icon"] } size={ 15 } />
      Batch edit
    </span>
  </Button>
);

export const renderSelectAllButton = (selectedDocumentIds, onSelectAllDocuments, accountId, filters) => {
  if (selectedDocumentIds.length >= 20) {
    return (
      <Button link onClick={ () => onSelectAllDocuments(accountId, filters) }>select all</Button>
    );
  }
  return null;
};



// WEBPACK FOOTER //
// ./src/js/components/documents/documents-grid/multi-document-actions-container.jsx